import { Button, Dropdown, Menu } from 'antd';
import React, { useCallback, useState, useEffect } from 'react';
import {
  deviceModeTypeLabelMap,
  deviceStatusLabelMap,
  PartnerHigoDevice,
} from 'models/higoDevice';
import { commonPaginationConfig, initialPageParams } from 'models/page';
import { useQuery, useQueryCache } from 'react-query';
import { QueryKey } from 'api/queryKey';
import { BusinessPartnersAPI } from 'api/BusinessPartnersAPI';
import { BusinessPartnerContext } from 'pages/BusinessPartnerManagement/BusinessPartnerContext';
import { Header } from 'common/components/Header';
import { AssignDeviceToBusinessPartnerModal } from 'common/components/AssignDeviceToBusinessPartnerModal';
import { useEnsureContext } from 'common/hooks';
import { Link } from 'react-router-dom';
import Column from 'antd/lib/table/Column';
import { UnassignUserPartnerDeviceButton } from 'common/components/UnassignUserPartnerDevice';
import { SuperAdminGuard } from 'common/components/SuperAdminGuard';
import {
  CloseOutlined,
  DeleteOutlined,
  UserDeleteOutlined,
} from '@ant-design/icons';
import { UnassignPartnerDeviceButton } from 'common/components/UnassignPartnerDevice';
import { useRole } from 'common/hooks/useRole';
import { GenerateDeviceTokenButton } from 'common/components/device-tokens/GenerateDeviceTokenButton';
import { DeleteDeviceTokenButton } from 'common/components/device-tokens/DeleteDeviceTokenButton';
import { ShowDeviceTokenControl } from 'common/components/device-tokens/ShowDeviceTokenControl';
import { GenerateDevicesTokensButton } from 'common/components/device-tokens/GenerateDevicesTokensButton';
import { AdministrationGuard } from 'common/components/AdministrationGuard';
import { DownloadDeviceTokensCsvFileButton } from 'common/components/device-tokens/DownloadDeviceTokensCsvFileButton';
import { useFormatMessage } from 'common/hooks/useFormatMessage';
import { FormattedMessage } from 'react-intl';
import { NumberedTable } from 'common/components/NumberedTable';
import { BusinessPartnersDevicesFilters } from 'pages/BusinessPartnerManagement/BusinessPartnerDevicesManagement/BusinessPartnerDevicesFilters';
import { purge } from 'lib/ramda/purge';
import { makeSubpartnersIdsString } from 'pages/BusinessPartnerManagement/BusinessPartnerMedicalOperatorsManagement/BusinessPartnerMedicalOperatorsPage/utils/makeSubpartnersIDsString';
import { isNil } from 'ramda';

export const BusinessPartnerDevicesPage = () => {
  const bpContext = useEnsureContext(BusinessPartnerContext);
  const intlMessage = useFormatMessage();

  const [pageMetaData, setPageMetaData] = useState(initialPageParams);
  const [filters, setFilters] = useState({});
  const [filteredPartnerId, setFilteredPartnerId] = useState(0);
  const [assignDeviceModalVisible, setAssignDeviceModalVisible] = useState(
    false
  );
  const role = useRole();
  const queryCache = useQueryCache();
  const invalidateList = useCallback(() => {
    queryCache.invalidateQueries(QueryKey.BusinessPartnerDevicesList).then();
  }, [queryCache]);
  const invalidateDevicesList = useCallback(() => {
    queryCache.invalidateQueries(QueryKey.DevicesList).then();
  }, [queryCache]);
  const invalidateToken = useCallback(
    (deviceId) => {
      queryCache.removeQueries(QueryKey.DeviceToken, deviceId);
    },
    [queryCache]
  );

  const { isMedicalOperator } = useRole();
  const shouldHidePartnerColumn =
    !isNil(bpContext.parentPartnerId) || isMedicalOperator;
  const invalidateDevicesToAssignList = useCallback(() => {
    queryCache.invalidateQueries(QueryKey.SelectDeviceModalDevicesList).then();
  }, [queryCache]);

  const IDlist: number[] = [];
  const [IDsToGetData, setIDsToGetData] = useState('');

  const {
    data: subpartnersData,
    isLoading: isLoadingSubpartners,
    isSuccess: isSubpartnersSuccess,
  } = useQuery(
    [QueryKey.BusinessPartnersList, pageMetaData, filters],
    () =>
      BusinessPartnersAPI.fetchSubpartners(bpContext.id, {
        ...pageMetaData,
        ...filters,
      }),
    {
      onSuccess: (res) => {
        const IDsString = makeSubpartnersIdsString(res, IDlist);
        setIDsToGetData(IDsString);
        invalidateDevicesList();
        invalidateList();
        invalidateDevicesToAssignList();
      },
      keepPreviousData: true,
    }
  );

  const { isFetching, data, refetch } = useQuery(
    [QueryKey.BusinessPartnerDevicesList, pageMetaData, filters, IDsToGetData],
    () =>
      BusinessPartnersAPI.fetchDevices(
        filteredPartnerId !== 0 ? filteredPartnerId : bpContext.id,
        {
          subPartnersIds: filteredPartnerId !== 0 ? '' : IDsToGetData,
          ...pageMetaData,
          ...filters,
        }
      ),
    {
      enabled: isSubpartnersSuccess,
      keepPreviousData: true,
    }
  );

  const totalDataNumber = data?.totalElements;

  useEffect(() => {
    refetch();
  }, [totalDataNumber, subpartnersData, IDsToGetData, refetch]);

  const paginationConfig = {
    ...commonPaginationConfig,
    current: pageMetaData?.page,
    pageSize: data?.size,
    total: data?.totalElements,
    onChange: (page: number, pageSize?: number) =>
      setPageMetaData((prev) => ({
        ...prev,
        page,
        ...(pageSize && { pageSize }),
      })),
  };

  return (
    <>
      <Header
        title={intlMessage({ id: 'devices.partner' })}
        extra={[
          <SuperAdminGuard key="1">
            <Button
              type="primary"
              onClick={() => setAssignDeviceModalVisible(true)}
            >
              <FormattedMessage id="devices.assignNew" />
            </Button>
            <AssignDeviceToBusinessPartnerModal
              visible={assignDeviceModalVisible}
              title={intlMessage({ id: 'devices.assignToPartner' })}
              destroyOnClose={true}
              afterClose={() => {
                invalidateDevicesList();
                invalidateList();
                invalidateDevicesToAssignList();
                refetch();
              }}
              businessPartnerId={bpContext.id}
              onCancel={() => setAssignDeviceModalVisible(false)}
              footer={null}
            />
          </SuperAdminGuard>,
          <AdministrationGuard key="2">
            <GenerateDevicesTokensButton onGenerate={invalidateList}>
              <FormattedMessage id="devices.generateTokens" />
            </GenerateDevicesTokensButton>
            <DownloadDeviceTokensCsvFileButton />
          </AdministrationGuard>,
        ]}
      />

      <BusinessPartnersDevicesFilters
        style={{ marginBottom: '1rem' }}
        shouldHidePartnerColumn={shouldHidePartnerColumn}
        onSubmit={(values) => {
          (values.partnerID === 0 || !values.partnerID) &&
            setFilteredPartnerId(0);
          values.partnerID && setFilteredPartnerId(values.partnerID);
          setPageMetaData((pageMetaData) => ({ ...pageMetaData, page: 1 }));
          const shuldFilterWithoutDeviceStatuAndMode =
            values.modeType === 'both' && values.deviceStatus === 'all';
          const { deviceStatus, ...valuesWithoutDeviceStatus } = values;
          const { modeType, ...valuesWithoutModeType } = values;
          const valuesWithoutDeviceStatusAndMode = (({
            deviceStatus,
            modeType,
            ...obj
          }) => obj)(values);
          if (shuldFilterWithoutDeviceStatuAndMode) {
            setFilters(purge(valuesWithoutDeviceStatusAndMode));
          } else if (values.deviceStatus === 'all') {
            setFilters(purge(valuesWithoutDeviceStatus));
          } else if (values.modeType === 'both') {
            setFilters(purge(valuesWithoutModeType));
          } else {
            setFilters(purge(values));
          }
        }}
      />

      <NumberedTable
        rowKey="id"
        loading={isLoadingSubpartners || isFetching}
        dataSource={data?.content}
        pagination={paginationConfig}
        scroll={{ x: 'auto' }}
      >
        {!isLoadingSubpartners && !isFetching && (
          <>
            <Column
              title={intlMessage({ id: 'deviceSerialNumber' })}
              dataIndex="serialNumber"
              render={(value) =>
                value !== null && value !== '' ? value : '--'
              }
            />
            <Column
              title={intlMessage({ id: 'devices.boxSerialNumber' })}
              dataIndex="boxSerialNumber"
              render={(value) =>
                value !== null && value !== '' ? value : '--'
              }
            />
            {!shouldHidePartnerColumn && (
              <Column
                title={intlMessage({ id: 'partner' })}
                dataIndex={['partnerDetails', 'name']}
              />
            )}

            <Column
              title={intlMessage({ id: 'devices.activeToken' })}
              align="center"
              width={180}
              render={(value, record: PartnerHigoDevice) =>
                record.hasToken ? (
                  <ShowDeviceTokenControl device={record} />
                ) : record.modeType === 'FAMILY_MODE' ? (
                  <CloseOutlined />
                ) : (
                  'n/a'
                )
              }
            />
            <Column
              title={intlMessage({ id: 'status' })}
              dataIndex="status"
              align="center"
              render={(value) =>
                intlMessage({ id: deviceStatusLabelMap.get(value) ?? value })
              }
            />
            <Column
              title={intlMessage({ id: 'mode' })}
              dataIndex="modeType"
              align="center"
              render={(value) => (
                <p>
                  {intlMessage({
                    id: deviceModeTypeLabelMap.get(value) ?? value,
                  })}
                </p>
              )}
            />
            <Column
              title={intlMessage({ id: 'devices.firmware' })}
              dataIndex="firmwareVersion"
              align="center"
              render={(value) => (value !== null ? value : '--')}
            />
            <Column
              title={intlMessage({ id: 'assigned_to' })}
              dataIndex="assignedUserId"
              align="right"
              render={(value, record: PartnerHigoDevice) =>
                record.assignedUser ? (
                  <Link
                    key={`medical-operators/${record.id}`}
                    to={`${
                      record.modeType === 'PRO_MODE'
                        ? 'medical-operators'
                        : 'family-users'
                    }/${record.assignedUser.userId}`}
                  >
                    {record.assignedUser.firstName}{' '}
                    {record.assignedUser.lastName}
                  </Link>
                ) : (
                  '--'
                )
              }
            />
            {role.isAdministrationRole && (
              <Column
                title={intlMessage({ id: 'actions' })}
                dataIndex="assignedUserId"
                align="right"
                render={(value, record: PartnerHigoDevice) => {
                  return record.modeType === 'PRO_MODE' &&
                    !role.isSuperAdmin &&
                    !record.assignedUser ? null : (
                    <Dropdown.Button
                      overlay={
                        <Menu>
                          {record.modeType === 'FAMILY_MODE' &&
                            !record.hasToken && (
                              <Menu.Item key="generate-token">
                                <GenerateDeviceTokenButton
                                  device={record}
                                  tooltip={null}
                                  type="text"
                                  onGenerate={invalidateList}
                                >
                                  <FormattedMessage
                                    id="devices.generateToken"
                                    tagName="span"
                                  />
                                </GenerateDeviceTokenButton>
                              </Menu.Item>
                            )}

                          {record.modeType === 'FAMILY_MODE' &&
                            record.hasToken && (
                              <Menu.Item key="delete-token">
                                <DeleteDeviceTokenButton
                                  device={record}
                                  tooltip={null}
                                  type="text"
                                  onDelete={(deviceId) => {
                                    invalidateToken(deviceId);
                                    invalidateList();
                                  }}
                                >
                                  <FormattedMessage
                                    id="devices.deleteToken"
                                    tagName="span"
                                  />
                                </DeleteDeviceTokenButton>
                              </Menu.Item>
                            )}
                          {record.assignedUser && (
                            <Menu.Item key="unassign-user-partner-device">
                              <UnassignUserPartnerDeviceButton
                                businessPartnerId={bpContext.id}
                                device={record}
                                user={record.assignedUser}
                                onUnassign={invalidateList}
                                icon={<UserDeleteOutlined />}
                                tooltip={null}
                                type="text"
                              >
                                <FormattedMessage
                                  id="devices.unassign.user"
                                  tagName="span"
                                />
                              </UnassignUserPartnerDeviceButton>
                            </Menu.Item>
                          )}
                          {role.isSuperAdmin && (
                            <Menu.Item key="unassign-partner-device">
                              <UnassignPartnerDeviceButton
                                danger
                                businessPartnerCtx={bpContext}
                                device={record}
                                onUnassign={() => {
                                  invalidateList();
                                  invalidateDevicesToAssignList();
                                }}
                                icon={<DeleteOutlined />}
                                tooltip={null}
                                type="text"
                              >
                                <FormattedMessage
                                  id="devices.unassign.partner"
                                  tagName="span"
                                />
                              </UnassignPartnerDeviceButton>
                            </Menu.Item>
                          )}
                        </Menu>
                      }
                    />
                  );
                }}
              />
            )}
          </>
        )}
      </NumberedTable>
    </>
  );
};
